import { FC, useState } from 'react';
import { SimulationParameters, SimulationResults, PortfolioProperty, PortfolioSummary, UserSettings, Property, PortfolioSimulation } from '@/types/index';

interface Props {
  property?: PortfolioProperty;
  catalogProperty?: Property;
  userSettings?: UserSettings;
  portfolioSummary?: PortfolioSummary;
  defaultAmortizationPct?: number;
  editingSimulation?: PortfolioSimulation;
  onSave: (name: string, parameters: SimulationParameters) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

function useNum(init?: number | null) {
  const [raw, setRaw] = useState(init != null ? String(init) : '');
  const val = raw === '' ? undefined : parseFloat(raw);
  return [raw, setRaw, val] as const;
}

const fmtChf = (v: number | null | undefined) =>
  v == null || isNaN(v) ? '—' : `CHF ${new Intl.NumberFormat('fr-CH').format(Math.round(v))}`;
const fmtPct = (v: number | null | undefined) =>
  v == null || isNaN(v) ? '—' : `${v.toFixed(2)} %`;

function computeLive(p: SimulationParameters): SimulationResults | null {
  if (!p.purchasePrice || !p.downPaymentPct || !p.interestRate) return null;
  const loanAmount = p.purchasePrice * (1 - p.downPaymentPct / 100);
  const equityRequired = p.purchasePrice * (p.downPaymentPct / 100) + (p.acquisitionCosts ?? 0);
  const effectiveRent = (p.grossAnnualRent ?? 0) * (1 - (p.vacancyRate ?? 0) / 100);
  const NOI = effectiveRent - (p.annualCharges ?? 0);
  const annualInterest = loanAmount * (p.interestRate / 100);
  const annualAmortization = loanAmount * ((p.annualAmortizationPct ?? 1) / 100);
  const cashFlow = NOI - annualInterest - annualAmortization;
  const cashFlowMonthly = cashFlow / 12;
  const LTV = loanAmount / p.purchasePrice;
  const capRate = p.purchasePrice > 0 ? (NOI / p.purchasePrice) * 100 : null;
  const netYield = p.purchasePrice > 0 ? (NOI / p.purchasePrice) * 100 : null;
  const cashOnCash = equityRequired > 0 ? (cashFlow / equityRequired) * 100 : null;
  return { loanAmount, equityRequired, effectiveRent, NOI, annualInterest, annualAmortization,
           cashFlow, cashFlowMonthly, LTV, capRate, netYield, cashOnCash };
}

interface PortfolioImpact {
  // Tier 1
  cashFlowMonthlyBefore: number; cashFlowMonthlyAfter: number;
  ltvBefore: number | null; ltvAfter: number;
  noiBefore: number; noiAfter: number;
  equityBefore: number; equityAfter: number;
  // Tier 2
  debtBefore: number; debtAfter: number;
  valueBefore: number; valueAfter: number;
  capRateBefore: number | null; capRateAfter: number | null;
}

function computeImpact(summary: PortfolioSummary, res: SimulationResults, purchasePrice: number): PortfolioImpact {
  const newTotalDebt = summary.totalDebt + res.loanAmount;
  const newTotalValue = summary.totalMarketValue + purchasePrice;
  const newCapRate = res.capRate;
  const newAvgCapRate = summary.avgCapRate != null && newCapRate != null
    ? (summary.avgCapRate * summary.propertyCount + newCapRate) / (summary.propertyCount + 1)
    : (newCapRate ?? summary.avgCapRate);

  return {
    cashFlowMonthlyBefore: summary.totalCashFlow / 12,
    cashFlowMonthlyAfter: (summary.totalCashFlow + res.cashFlow) / 12,
    ltvBefore: summary.globalLTV,
    ltvAfter: newTotalValue > 0 ? newTotalDebt / newTotalValue : 0,
    noiBefore: summary.totalNOI,
    noiAfter: summary.totalNOI + res.NOI,
    equityBefore: summary.totalEquity,
    equityAfter: summary.totalEquity + res.equityRequired,
    debtBefore: summary.totalDebt,
    debtAfter: newTotalDebt,
    valueBefore: summary.totalMarketValue,
    valueAfter: newTotalValue,
    capRateBefore: summary.avgCapRate,
    capRateAfter: newAvgCapRate,
  };
}

const Field: FC<{ label: string; value: string; onChange: (v: string) => void; suffix?: string; hint?: string }> = (
  { label, value, onChange, suffix, hint }
) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">{label}</label>
    <div className="relative flex items-center">
      <input
        type="number"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      {suffix && <span className="absolute right-3 text-xs text-gray-400 pointer-events-none">{suffix}</span>}
    </div>
    {hint && <p className="mt-0.5 text-xs text-blue-600 dark:text-blue-400 font-medium">{hint}</p>}
  </div>
);

const KpiCard: FC<{ label: string; value: string; positive?: boolean; negative?: boolean }> = (
  { label, value, positive, negative }
) => (
  <div className="flex flex-col items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
    <span className="text-xs text-gray-500 dark:text-gray-400 mb-1 text-center">{label}</span>
    <span className={`text-sm font-bold ${positive ? 'text-green-600 dark:text-green-400' : negative ? 'text-red-500 dark:text-red-400' : 'text-gray-800 dark:text-gray-100'}`}>
      {value}
    </span>
  </div>
);

const ImpactChip: FC<{
  label: string;
  before: string;
  after: string;
  positiveWhenUp?: boolean; // true = hausse = bien (NOI, cashflow, equity, value), false = hausse = mauvais (LTV, dette)
  deltaPositive: boolean;
}> = ({ label, before, after, deltaPositive }) => (
  <div className="flex flex-col gap-0.5 px-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 min-w-[120px]">
    <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">{label}</span>
    <div className="flex items-center gap-1 text-[10px]">
      <span className="text-gray-500 dark:text-gray-400">{before}</span>
      <span className="text-gray-400">→</span>
      <span className={`font-bold ${deltaPositive ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
        {after}
      </span>
    </div>
  </div>
);

export const SimulationForm: FC<Props> = ({ property, catalogProperty, userSettings, portfolioSummary, defaultAmortizationPct, editingSimulation, onSave, onCancel, isSaving }) => {
  const [name, setName] = useState(editingSimulation?.name ?? '');

  // Si on édite, on récupère les paramètres existants ; sinon on pré-remplit depuis property/catalogProperty
  const ep = editingSimulation?.parameters;

  const initPrice = ep?.purchasePrice ?? property?.acquisitionPrice ?? catalogProperty?.askingPrice;
  const initRent = ep?.grossAnnualRent
    ?? property?.grossAnnualRent
    ?? (catalogProperty?.isRented && catalogProperty?.rentPrice ? catalogProperty.rentPrice * 12
      : catalogProperty?.estimatedRent ? catalogProperty.estimatedRent * 12 : undefined);
  const initCharges = ep?.annualCharges
    ?? (property
      ? (property.ppeChargesAnnual ?? 0) + (property.maintenanceCosts ?? 0) + (property.propertyTax ?? 0) + (property.otherCosts ?? 0)
      : catalogProperty?.ppeCharges ? catalogProperty.ppeCharges * 12 : undefined);

  const [priceRaw, setPrice, purchasePrice] = useNum(initPrice);
  const [costsRaw, setCosts, acquisitionCosts] = useNum(
    ep?.acquisitionCosts
    ?? (initPrice && userSettings ? Math.round(initPrice * userSettings.acquisitionFees / 100) : undefined)
  );
  const [downPayRaw, setDownPay, downPaymentPct] = useNum(ep?.downPaymentPct ?? userSettings?.equityRate ?? 20);
  const [rateRaw, setRate, interestRate] = useNum(
    ep?.interestRate ?? property?.interestRate ?? userSettings?.mortgageRate ?? 1.5
  );
  const [amortRaw, setAmort, annualAmortizationPct] = useNum(
    ep?.annualAmortizationPct ?? defaultAmortizationPct ?? userSettings?.amortizationRate ?? 1.0
  );
  const [rentRaw, setRent, grossAnnualRent] = useNum(initRent);
  const [vacancyRaw, setVacancy, vacancyRate] = useNum(ep?.vacancyRate ?? property?.vacancyRate ?? 0);
  const [chargesRaw, setCharges, annualCharges] = useNum(initCharges);

  const loanAmount = purchasePrice ? purchasePrice * (1 - (downPaymentPct ?? 20) / 100) : undefined;

  const params: SimulationParameters = {
    purchasePrice: purchasePrice ?? 0,
    acquisitionCosts,
    downPaymentPct: downPaymentPct ?? 20,
    interestRate: interestRate ?? 0,
    annualAmortizationPct: annualAmortizationPct ?? 1,
    grossAnnualRent,
    vacancyRate,
    annualCharges,
  };

  const res = computeLive(params);
  const impact = portfolioSummary && res && purchasePrice
    ? computeImpact(portfolioSummary, res, purchasePrice)
    : null;

  const handleSave = async () => {
    if (!name.trim() || !purchasePrice) return;
    await onSave(name.trim(), params);
  };

  return (
    <div className="space-y-5">
      {/* Nom du scénario */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
          Nom du scénario <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Ex : Acquisition 20% apport, taux 1.5%"
          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        {/* Colonne gauche — paramètres */}
        <div className="space-y-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Paramètres</p>
          <Field label="Prix d'achat" value={priceRaw} onChange={setPrice} suffix="CHF" />
          <Field label="Frais d'acquisition" value={costsRaw} onChange={setCosts} suffix="CHF"
            hint="Notaire, registre foncier, courtage…" />
          <Field label="Apport propre" value={downPayRaw} onChange={setDownPay} suffix="%"
            hint={purchasePrice && downPaymentPct ? `= ${fmtChf(purchasePrice * downPaymentPct / 100)}` : undefined} />
          <Field label="Taux d'intérêt" value={rateRaw} onChange={setRate} suffix="%" />
          <Field label="Amortissement annuel" value={amortRaw} onChange={setAmort} suffix="%/an"
            hint={loanAmount && annualAmortizationPct ? `= ${fmtChf(loanAmount * annualAmortizationPct / 100)}/an` : undefined} />
          <Field label="Loyer brut annuel" value={rentRaw} onChange={setRent} suffix="CHF"
            hint={grossAnnualRent ? `≈ ${fmtChf(grossAnnualRent / 12)}/mois` : undefined} />
          <Field label="Taux de vacance" value={vacancyRaw} onChange={setVacancy} suffix="%" />
          <Field label="Charges annuelles totales" value={chargesRaw} onChange={setCharges} suffix="CHF"
            hint="PPE + entretien + impôt foncier + autres" />
        </div>

        {/* Colonne droite — résultats live actif */}
        <div className="space-y-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Résultats calculés</p>
          {res ? (
            <div className="grid grid-cols-2 gap-2">
              <KpiCard label="Fonds propres requis" value={fmtChf(res.equityRequired)} />
              <KpiCard label="Montant du prêt" value={fmtChf(res.loanAmount)} />
              <KpiCard label="LTV" value={`${(res.LTV * 100).toFixed(1)} %`}
                negative={res.LTV > 0.8} positive={res.LTV <= 0.65} />
              <KpiCard label="Intérêts annuels" value={fmtChf(res.annualInterest)} />
              <KpiCard label="Amortissement annuel" value={fmtChf(res.annualAmortization)} />
              <KpiCard label="NOI annuel" value={fmtChf(res.NOI)}
                positive={res.NOI > 0} negative={res.NOI <= 0} />
              <KpiCard label="Cash-flow / mois" value={fmtChf(res.cashFlowMonthly)}
                positive={res.cashFlowMonthly > 0} negative={res.cashFlowMonthly < 0} />
              <KpiCard label="Rendement net" value={fmtPct(res.netYield)} />
              <KpiCard label="Cash-on-cash" value={fmtPct(res.cashOnCash)}
                positive={(res.cashOnCash ?? 0) > 0} negative={(res.cashOnCash ?? 0) < 0} />
              <KpiCard label="Cap rate" value={fmtPct(res.capRate)} />
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-400 text-center px-4">Renseignez le prix, l'apport et le taux pour voir les résultats</p>
            </div>
          )}
        </div>
      </div>

      {/* Section impact portefeuille global */}
      {impact && (
        <div className="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Impact sur le portefeuille global
          </p>

          {/* Tier 1 */}
          <div className="flex flex-wrap gap-2">
            <ImpactChip
              label="Cash-flow mensuel total"
              before={fmtChf(impact.cashFlowMonthlyBefore)}
              after={fmtChf(impact.cashFlowMonthlyAfter)}
              deltaPositive={impact.cashFlowMonthlyAfter >= impact.cashFlowMonthlyBefore}
            />
            <ImpactChip
              label="LTV globale"
              before={impact.ltvBefore != null ? `${(impact.ltvBefore * 100).toFixed(1)} %` : '—'}
              after={`${(impact.ltvAfter * 100).toFixed(1)} %`}
              deltaPositive={impact.ltvAfter <= (impact.ltvBefore ?? 0)}
            />
            <ImpactChip
              label="NOI annuel total"
              before={fmtChf(impact.noiBefore)}
              after={fmtChf(impact.noiAfter)}
              deltaPositive={impact.noiAfter >= impact.noiBefore}
            />
            <ImpactChip
              label="Équité totale"
              before={fmtChf(impact.equityBefore)}
              after={fmtChf(impact.equityAfter)}
              deltaPositive={impact.equityAfter >= impact.equityBefore}
            />
          </div>

          {/* Tier 2 */}
          <div className="flex flex-wrap gap-2">
            <ImpactChip
              label="Dette totale"
              before={fmtChf(impact.debtBefore)}
              after={fmtChf(impact.debtAfter)}
              deltaPositive={false}
            />
            <ImpactChip
              label="Valeur du parc"
              before={fmtChf(impact.valueBefore)}
              after={fmtChf(impact.valueAfter)}
              deltaPositive={true}
            />
            <ImpactChip
              label="Cap rate moyen"
              before={fmtPct(impact.capRateBefore)}
              after={fmtPct(impact.capRateAfter)}
              deltaPositive={(impact.capRateAfter ?? 0) >= (impact.capRateBefore ?? 0)}
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || !name.trim() || !purchasePrice}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Sauvegarde…' : editingSimulation ? 'Mettre à jour' : 'Sauvegarder le scénario'}
        </button>
      </div>
    </div>
  );
};
